// UC 34260 — Production Schedule & Kitchen Slot Booking
// UC 34256 — Record Production Completion and Batch Yield
// Mirrors RawHTMLDesign/production-schedule-&-kitchen-slot-booking/production-schedule-&-slot-calendar-page.html

import { useEffect, useMemo, useState, useCallback } from 'react';
import api from '../../../services/api.js';
import { MEAL_PERIODS } from '../../../utils/mealPeriods.js';
import './ProductionScheduleAndSlotCalendarPage.css';

// Brand colour palette — matches RawHTMLDesign event-brand-* CSS classes
const BRAND_COLORS = [
  '#e74c3c', // red
  '#f0ad4e', // amber
  '#2ecc71', // green
  '#3498db', // blue
  '#9b59b6', // purple
  '#e67e22', // orange
  '#1abc9c', // teal
  '#e91e63', // pink
];
function brandColor(brandId, brands) {
  const idx = brands.findIndex((b) => b.id === brandId);
  return BRAND_COLORS[idx >= 0 ? idx % BRAND_COLORS.length : 0];
}

// Format HH:MM → 12-hour AM/PM
function fmt12(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ampm}`;
}

// Return Monday of the week containing `date`
function weekStart(date) {
  const d = new Date(date);
  const day = d.getDay(); // 0=Sun
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d;
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

function fmtRange(mon) {
  const sun = addDays(mon, 6);
  const opts = { month: 'short', day: 'numeric' };
  return `${mon.toLocaleDateString('en-US', opts)} - ${sun.toLocaleDateString('en-US', opts)}`;
}

function statusBadgeClass(s) {
  switch (s) {
    case 'Scheduled': return 'badge-scheduled';
    case 'In Production': return 'badge-running';
    case 'Completed': return 'badge-completed';
    case 'Cancelled': return 'badge-secondary';
    default: return 'badge-light';
  }
}

const QUALITY_OPTIONS = [
  'Approved (Meets standard recipe taste/color/BOM criteria)',
  'Conditionally Approved (Marginal but acceptable)',
  'Rejected (Sub-standard, scrap batch fully)',
];

const DAY_LABELS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const STATUS_OPTIONS = ['ALL', 'Scheduled', 'In Production', 'Completed', 'Cancelled'];

function today() {
  return new Date().toISOString().slice(0, 10);
}

// ── Run Detail Modal ─────────────────────────────────────────
function RunDetailModal({ run, brands, onClose, onStart, onComplete, onCancel }) {
  if (!run) return null;
  const color = brandColor(run.brand_id, brands);
  const sNames = run.station_names || [run.station_name].filter(Boolean);
  const isScheduled = run.status === 'Scheduled';
  const isInProd = run.status === 'In Production';
  const isCompleted = run.status === 'Completed';

  return (
    <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: 1060 }} data-testid="run-detail-modal">
      <div className="modal-dialog modal-dialog-centered modal-dialog-scrollable" style={{ maxWidth: 500 }}>
        <div className="modal-content shadow-lg border-0" style={{ borderRadius: 14 }}>
          <div className="modal-header" style={{ background: color, color: '#fff', borderRadius: '14px 14px 0 0' }}>
            <h5 className="modal-title font-weight-bold">
              <i className="fa-solid fa-clipboard-list mr-2" />Production Run Details
            </h5>
            <button type="button" className="close" style={{ color: '#fff', opacity: 1 }} onClick={onClose} data-testid="run-detail-close">
              <span>&times;</span>
            </button>
          </div>
          <div className="modal-body p-4">
            {/* Status badge */}
            <div className="mb-3">
              <span className={`badge badge-status ${statusBadgeClass(run.status)} px-3 py-2`} style={{ fontSize: '0.8rem' }}>
                {run.status.toUpperCase()}
              </span>
            </div>

            {/* Info grid */}
            <div className="row" style={{ fontSize: '0.875rem', rowGap: 10 }}>
              <div className="col-6">
                <div className="text-muted small font-weight-600 text-uppercase mb-1">Brand</div>
                <div className="font-weight-bold" style={{ color }}>{run.brand_name}</div>
              </div>
              <div className="col-6">
                <div className="text-muted small font-weight-600 text-uppercase mb-1">SKU</div>
                <div className="font-weight-bold">{run.item_code}</div>
                <div className="text-muted small">{run.item_name}</div>
              </div>
              <div className="col-6">
                <div className="text-muted small font-weight-600 text-uppercase mb-1">Date</div>
                <div className="font-weight-bold">{String(run.production_date).slice(0, 10)}</div>
              </div>
              <div className="col-6">
                <div className="text-muted small font-weight-600 text-uppercase mb-1">Time Slot</div>
                <div className="font-weight-bold">{fmt12(run.start_time)} – {fmt12(run.end_time)}</div>
              </div>
              <div className="col-6">
                <div className="text-muted small font-weight-600 text-uppercase mb-1">Planned Qty</div>
                <div className="font-weight-bold text-teal">{Number(run.planned_yield).toFixed(0)} units</div>
              </div>
              <div className="col-6">
                {isCompleted && (
                  <>
                    <div className="text-muted small font-weight-600 text-uppercase mb-1">Actual Yield</div>
                    <div className="font-weight-bold text-success">{Number(run.actual_yield).toFixed(0)} units</div>
                  </>
                )}
              </div>
              <div className="col-12">
                <div className="text-muted small font-weight-600 text-uppercase mb-1">Station(s)</div>
                <div>{sNames.map((n) => <span key={n} className="badge badge-station mr-1">{n}</span>)}</div>
              </div>
              {run.quality_evaluation && (
                <div className="col-12">
                  <div className="text-muted small font-weight-600 text-uppercase mb-1">Quality</div>
                  <div className="font-weight-bold">{run.quality_evaluation}</div>
                </div>
              )}
              {run.variance_note && (
                <div className="col-12">
                  <div className="text-muted small font-weight-600 text-uppercase mb-1">Variance Note</div>
                  <div className="text-muted small">{run.variance_note}</div>
                </div>
              )}
            </div>
          </div>
          <div className="modal-footer bg-light" style={{ gap: 8 }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
            {isScheduled && (
              <button type="button" className="btn btn-warning" onClick={() => { onStart(run.id); onClose(); }} data-testid="run-detail-start">
                <i className="fa-solid fa-play mr-1" />Start Run
              </button>
            )}
            {(isScheduled || isInProd) && (
              <>
                <button type="button" className="btn btn-teal" onClick={() => { onComplete(run); onClose(); }} data-testid="run-detail-complete">
                  <i className="fa-solid fa-check mr-1" />Log Completion
                </button>
                <button type="button" className="btn btn-outline-danger" onClick={() => { onCancel(run.id); onClose(); }} data-testid="run-detail-cancel">
                  Cancel Run
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Weekly Calendar Grid ─────────────────────────────────────
function WeeklyGrid({ runs, stations, brands, weekOffset, setWeekOffset, onCardClick }) {
  const [brandFilter, setBrandFilter] = useState('');

  const mon = useMemo(() => {
    const base = weekStart(new Date());
    return addDays(base, weekOffset * 7);
  }, [weekOffset]);

  const weekDates = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(mon, i)), [mon]);

  // Build cell map: stationId → isoDate → [runs]
  const cellMap = useMemo(() => {
    const map = {};
    for (const r of runs) {
      if (brandFilter && r.brand_id !== brandFilter) continue;
      if (!['Scheduled', 'In Production'].includes(r.status)) continue;
      const date = isoDate(new Date(r.production_date));
      const sids = r.station_ids?.length ? r.station_ids : [r.station_id].filter(Boolean);
      for (const sid of sids) {
        if (!map[sid]) map[sid] = {};
        if (!map[sid][date]) map[sid][date] = [];
        map[sid][date].push(r);
      }
    }
    return map;
  }, [runs, brandFilter]);

  return (
    <div className="card shadow-sm mb-4" data-testid="weekly-calendar">
      <div className="card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between flex-wrap">
        <h5 className="card-title mb-0 font-weight-bold text-dark">
          <i className="fa-solid fa-calendar-week mr-2 text-teal" />Kitchen Station Scheduler <span className="text-muted font-weight-normal">(Weekly Grid)</span>
        </h5>
        <div className="d-flex align-items-center mt-2 mt-md-0" style={{ gap: 8 }}>
          <select
            className="form-control form-control-sm custom-select"
            style={{ minWidth: 130 }}
            value={brandFilter}
            onChange={(e) => setBrandFilter(e.target.value)}
            data-testid="calendar-brand-filter"
          >
            <option value="">All Brands</option>
            {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <button className="btn btn-sm btn-outline-secondary px-2" onClick={() => setWeekOffset((w) => w - 1)} data-testid="week-prev">
            <i className="fa-solid fa-chevron-left" />
          </button>
          <span className="small font-weight-600 text-nowrap px-1" data-testid="week-label">{fmtRange(mon)}</span>
          <button className="btn btn-sm btn-outline-secondary px-2" onClick={() => setWeekOffset((w) => w + 1)} data-testid="week-next">
            <i className="fa-solid fa-chevron-right" />
          </button>
        </div>
      </div>
      <div className="card-body p-0">
        <div className="calendar-scroll">
          <table className="table mb-0 scheduler-grid" data-testid="scheduler-grid-table">
            <thead>
              <tr>
                <th className="station-header">STATION / ROOM</th>
                {weekDates.map((d, i) => (
                  <th key={i} className={`day-header${isoDate(d) === today() ? ' day-today' : ''}`}>
                    {DAY_LABELS[i]}
                    <div className="day-date">{d.getDate()}</div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {stations.length === 0 && (
                <tr><td colSpan={8} className="text-center text-muted py-4">No stations configured.</td></tr>
              )}
              {stations.map((station) => (
                <tr key={station.id} data-testid={`grid-station-${station.id}`}>
                  <td className="station-cell">{station.name}</td>
                  {weekDates.map((d, i) => {
                    const dateKey = isoDate(d);
                    const cellRuns = cellMap[station.id]?.[dateKey] || [];
                    return (
                      <td key={i} className="grid-slot">
                        {cellRuns.map((r) => {
                          const color = brandColor(r.brand_id, brands);
                          return (
                            <div
                              key={r.id}
                              className="calendar-event-card"
                              style={{ background: color, borderColor: color, cursor: 'pointer' }}
                              data-testid={`calendar-event-${r.id}`}
                              title={`${r.brand_name} — ${r.item_code} ${r.item_name}\n${r.start_time}–${r.end_time}`}
                              onClick={() => onCardClick(r)}
                            >
                              <div className="event-brand">{r.brand_name}</div>
                              <div className="event-sku">{r.item_code}</div>
                              <div className="event-time">{r.start_time} - {r.end_time}</div>
                            </div>
                          );
                        })}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


// ── Main Page ────────────────────────────────────────────────
export default function ProductionScheduleAndSlotCalendarPage() {
  const [brands, setBrands] = useState([]);
  const [items, setItems] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [stations, setStations] = useState([]);
  const [runs, setRuns] = useState([]);
  const [weekOffset, setWeekOffset] = useState(0);

  // Booking form
  const emptyForm = {
    brand_id: '',
    item_id: '',
    pull_order_id: '',
    production_date: today(),
    start_time: '08:00',
    end_time: '10:00',
    station_ids: [],
    planned_yield: '',
    planned_yield_unit: 'Units',
    // Serving cafeteria + daypart. Optional: a commissary run that is not tied to a
    // single cafeteria still schedules exactly as before.
    outlet_id: '',
    meal_period: '',
  };
  const [form, setForm] = useState(emptyForm);
  const [checkResult, setCheckResult] = useState(null); // null | 'available' | 'conflict'
  const [checking, setChecking] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [banner, setBanner] = useState({ message: '', kind: '' });

  // Batch table filters
  const [filterStatus, setFilterStatus] = useState('ALL');
  const [filterBrand, setFilterBrand] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  // Run detail modal (calendar card click)
  const [detailRun, setDetailRun] = useState(null);

  // Completion modal
  const [completionRun, setCompletionRun] = useState(null);
  const [actualYield, setActualYield] = useState('');
  const [varianceNote, setVarianceNote] = useState('');
  const [qualityEval, setQualityEval] = useState('');
  const [completing, setCompleting] = useState(false);
  const [completionError, setCompletionError] = useState('');
  const [negativeWarning, setNegativeWarning] = useState(null);

  const refresh = useCallback(async () => {
    const [bRes, iRes, sRes, rRes, oRes] = await Promise.all([
      api.get('/master-data/brands').then((x) => x.data.data || []).catch(() => []),
      api.get('/master-data/items').then((x) => (x.data.data || []).filter((i) => i.is_active !== false)).catch(() => []),
      api.get('/production-runs/stations').then((x) => x.data.data || []).catch(() => []),
      api.get('/production-runs').then((x) => x.data.data || []).catch(() => []),
      api.get('/master-data/outlets').then((x) => (x.data.data || []).filter((o) => o.is_active !== false)).catch(() => []),
    ]);
    setBrands(bRes);
    setItems(iRes);
    setStations(sRes);
    setRuns(rRes);
    setOutlets(oRes);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Filter items by selected brand
  const filteredItems = useMemo(
    () => form.brand_id ? items.filter((i) => !i.brand_id || i.brand_id === form.brand_id) : items,
    [items, form.brand_id],
  );

  const filteredRuns = useMemo(() => {
    return runs.filter((r) => {
      if (filterStatus !== 'ALL' && r.status !== filterStatus) return false;
      if (filterBrand && r.brand_id !== filterBrand) return false;
      if (filterDateFrom && isoDate(new Date(r.production_date)) < filterDateFrom) return false;
      if (filterDateTo && isoDate(new Date(r.production_date)) > filterDateTo) return false;
      return true;
    });
  }, [runs, filterStatus, filterBrand, filterDateFrom, filterDateTo]);

  const variancePct = useMemo(() => {
    if (!completionRun || !actualYield) return null;
    const planned = Number(completionRun.planned_yield);
    if (!planned) return null;
    const pct = ((Number(actualYield) - planned) / planned) * 100;
    return pct;
  }, [completionRun, actualYield]);

  const needsVarianceNote = variancePct !== null && Math.abs(variancePct) > 10;

  function toggleStation(id) {
    setForm((f) => ({
      ...f,
      station_ids: f.station_ids.includes(id)
        ? f.station_ids.filter((s) => s !== id)
        : [...f.station_ids, id],
    }));
    setCheckResult(null);
  }

  // "Check Slots" — client-side conflict detection
  async function checkSlots() {
    if (form.station_ids.length === 0 || !form.production_date || !form.start_time || !form.end_time) return;
    setChecking(true);
    setCheckResult(null);
    try {
      // Fetch runs for that date to check overlap
      const res = await api.get('/production-runs', { params: { date_start: form.production_date, date_end: form.production_date } });
      const dayRuns = res.data.data || [];
      const pStart = form.start_time;
      const pEnd = form.end_time;
      const hasConflict = dayRuns.some((r) => {
        if (!['Scheduled', 'In Production'].includes(r.status)) return false;
        const rSids = r.station_ids?.length ? r.station_ids : [r.station_id].filter(Boolean);
        const stationOverlap = form.station_ids.some((sid) => rSids.includes(sid));
        if (!stationOverlap) return false;
        return pStart < r.end_time && pEnd > r.start_time;
      });
      setCheckResult(hasConflict ? 'conflict' : 'available');
    } catch {
      setCheckResult(null);
    } finally {
      setChecking(false);
    }
  }

  async function saveSchedule() {
    setBanner({ message: '', kind: '' });
    if (!form.brand_id || !form.item_id || !form.production_date || form.station_ids.length === 0 || !form.planned_yield) {
      setBanner({ message: 'Please fill in all required fields and select at least one station.', kind: 'error' });
      return;
    }
    setScheduling(true);
    try {
      const res = await api.post('/production-runs', {
        brand_id: form.brand_id,
        item_id: form.item_id,
        pull_order_id: form.pull_order_id || null,
        production_date: form.production_date,
        start_time: form.start_time,
        end_time: form.end_time,
        station_ids: form.station_ids,
        station_id: form.station_ids[0],
        planned_yield: Number(form.planned_yield),
        outlet_id: form.outlet_id || undefined,
        meal_period: form.meal_period || undefined,
      });
      const hasConflict = res.data.has_conflict_warning;
      setBanner({
        message: hasConflict
          ? 'Scheduled. Warning: station conflict detected — review the calendar.'
          : 'Production run scheduled successfully.',
        kind: hasConflict ? 'warning' : 'success',
      });
      setForm(emptyForm);
      setCheckResult(null);
      await refresh();
    } catch (err) {
      setBanner({ message: err?.response?.data?.error || 'Failed to schedule run.', kind: 'error' });
    } finally {
      setScheduling(false);
    }
  }

  async function startRun(runId) {
    try {
      await api.patch(`/production-runs/${runId}/start`);
      await refresh();
    } catch (err) {
      setBanner({ message: err?.response?.data?.error || 'Start failed.', kind: 'error' });
    }
  }

  async function cancelRun(runId) {
    try {
      await api.patch(`/production-runs/${runId}/cancel`, { cancellation_reason: 'Cancelled by supervisor' });
      await refresh();
    } catch (err) {
      setBanner({ message: err?.response?.data?.error || 'Cancel failed.', kind: 'error' });
    }
  }

  function openCompletion(run) {
    setCompletionRun(run);
    setActualYield('');
    setVarianceNote('');
    setQualityEval('');
    setCompletionError('');
    setNegativeWarning(null);
  }

  async function submitCompletion(forceConfirm = false) {
    setCompletionError('');
    const yieldVal = Number(actualYield);
    if (!yieldVal || yieldVal <= 0) {
      setCompletionError('Actual yield must be greater than zero.');
      return;
    }
    if (needsVarianceNote && !varianceNote.trim()) {
      setCompletionError('A discrepancy note is required when variance exceeds 10%.');
      return;
    }
    setCompleting(true);
    try {
      const res = await api.post(`/production-runs/${completionRun.id}/complete`, {
        actual_yield: yieldVal,
        variance_note: varianceNote || null,
        quality_evaluation: qualityEval || null,
        confirm_negative: forceConfirm,
      });
      if (res.status === 202 && res.data.requires_confirmation) {
        setNegativeWarning(res.data.negative_items);
        setCompleting(false);
        return;
      }
      setCompletionRun(null);
      setBanner({ message: 'Production run completed. Stock deducted.', kind: 'success' });
      await refresh();
    } catch (err) {
      setCompletionError(err?.response?.data?.error || 'Completion failed.');
    } finally {
      setCompleting(false);
    }
  }

  return (
    <div className="container-fluid px-4 py-4" data-testid="production-schedule-page">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-start flex-wrap mb-4">
        <div>
          <h1 className="h3 font-weight-bold text-dark mb-1">
            <i className="fa-solid fa-calendar-check mr-2 text-teal" />
            Production Schedule &amp; Kitchen Slot Booking
          </h1>
          <p className="text-muted mb-0">Book kitchen stations, schedule production batches, check availability, and log completed yields.</p>
        </div>
        <span className="badge badge-light border px-3 py-2 text-dark shadow-sm mt-2 mt-sm-0">
          <i className="fa-solid fa-user-shield mr-1" />Role: <strong>Commissary Supervisor</strong>
        </span>
      </div>

      {/* Global banner */}
      {banner.message && (
        <div
          className={`alert mb-3 ${banner.kind === 'error' ? 'alert-danger' : banner.kind === 'warning' ? 'alert-warning' : 'alert-success'}`}
          data-testid={banner.kind === 'error' ? 'schedule-error' : 'schedule-success'}
        >
          {banner.message}
        </div>
      )}

      <div className="row">
        {/* ── LEFT: Booking Form ── */}
        <div className="col-xl-4 col-lg-5 mb-4">
          <div className="card shadow-sm">
            <div className="card-header bg-white py-3 border-bottom">
              <h5 className="card-title mb-0 font-weight-bold text-teal">
                <i className="fa-solid fa-circle-plus mr-2" />Schedule &amp; Book Station
              </h5>
            </div>
            <div className="card-body">
              {/* Production Date */}
              <div className="form-group">
                <label className="font-weight-600">
                  <i className="fa-solid fa-calendar-day mr-1 text-teal" />Production Date <span className="text-danger">*</span>
                </label>
                <input type="date" className="form-control" value={form.production_date}
                  onChange={(e) => { setForm((f) => ({ ...f, production_date: e.target.value })); setCheckResult(null); }}
                  data-testid="prod-date"
                />
              </div>

              {/* Brand + SKU */}
              <div className="form-row">
                <div className="form-group col-md-6">
                  <label className="font-weight-600">
                    <i className="fa-solid fa-store mr-1 text-teal" />Target Brand <span className="text-danger">*</span>
                  </label>
                  <select className="form-control custom-select" value={form.brand_id}
                    onChange={(e) => setForm((f) => ({ ...f, brand_id: e.target.value, item_id: '' }))}
                    data-testid="prod-brand"
                  >
                    <option value="">Choose Brand...</option>
                    {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </div>
                <div className="form-group col-md-6">
                  <label className="font-weight-600">
                    <i className="fa-solid fa-tag mr-1 text-teal" />Recipe SKU <span className="text-danger">*</span>
                  </label>
                  <select className="form-control custom-select" value={form.item_id}
                    onChange={(e) => setForm((f) => ({ ...f, item_id: e.target.value }))}
                    data-testid="prod-sku"
                  >
                    <option value="">Select SKU...</option>
                    {filteredItems.map((i) => <option key={i.id} value={i.id}>{i.item_code} — {i.name}</option>)}
                  </select>
                </div>
              </div>

              {/* Serving cafeteria + daypart — makes this run's ingredient usage
                  attributable to one cafeteria and one service period. */}
              <div className="form-row">
                <div className="form-group col-md-6">
                  <label className="font-weight-600">
                    <i className="fa-solid fa-utensils mr-1 text-teal" />Serving Cafeteria
                  </label>
                  <select className="form-control custom-select" value={form.outlet_id}
                    onChange={(e) => setForm((f) => ({ ...f, outlet_id: e.target.value }))}
                    data-testid="prod-outlet"
                  >
                    <option value="">Not cafeteria-specific</option>
                    {outlets
                      .filter((o) => !form.brand_id || o.brand_id === form.brand_id)
                      .map((o) => <option key={o.id} value={o.id}>{o.code} — {o.name}</option>)}
                  </select>
                </div>
                <div className="form-group col-md-6">
                  <label className="font-weight-600">
                    <i className="fa-solid fa-clock mr-1 text-teal" />Meal Period
                  </label>
                  <select className="form-control custom-select" value={form.meal_period}
                    onChange={(e) => setForm((f) => ({ ...f, meal_period: e.target.value }))}
                    data-testid="prod-meal-period"
                  >
                    <option value="">Unspecified</option>
                    {MEAL_PERIODS.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                  <small className="text-muted">
                    Sets which service period this batch is produced for.
                  </small>
                </div>
              </div>

              {/* Planned Qty */}
              <div className="form-group">
                <label className="font-weight-600">
                  <i className="fa-solid fa-boxes-stacking mr-1 text-teal" />Planned Quantity to Produce <span className="text-danger">*</span>
                </label>
                <div className="input-group">
                  <input type="number" className="form-control" min="1"
                    placeholder="Enter planned batch quantity"
                    value={form.planned_yield}
                    onChange={(e) => setForm((f) => ({ ...f, planned_yield: e.target.value }))}
                    data-testid="prod-qty"
                  />
                  <div className="input-group-append">
                    <span className="input-group-text">Units</span>
                  </div>
                </div>
                <small className="text-muted">Select a SKU to view default batch yield suggestions.</small>
              </div>

              {/* Start / End Time */}
              <div className="form-row">
                <div className="form-group col-6">
                  <label className="font-weight-600">
                    <i className="fa-solid fa-clock mr-1 text-teal" />Start Time <span className="text-danger">*</span>
                  </label>
                  <input type="time" className="form-control" value={form.start_time}
                    onChange={(e) => { setForm((f) => ({ ...f, start_time: e.target.value })); setCheckResult(null); }}
                    data-testid="prod-start-time"
                  />
                </div>
                <div className="form-group col-6">
                  <label className="font-weight-600">
                    <i className="fa-solid fa-rotate-left mr-1 text-teal" />End Time <span className="text-danger">*</span>
                  </label>
                  <input type="time" className="form-control" value={form.end_time}
                    onChange={(e) => { setForm((f) => ({ ...f, end_time: e.target.value })); setCheckResult(null); }}
                    data-testid="prod-end-time"
                  />
                </div>
              </div>

              {/* Station multi-select checkboxes */}
              <div className="form-group">
                <label className="font-weight-600">
                  <i className="fa-solid fa-kitchen-set mr-1 text-teal" />Select Kitchen Station / Slot <span className="text-danger">*</span>
                </label>
                <div className="station-checkbox-list" data-testid="station-checklist">
                  {stations.map((s) => (
                    <label key={s.id} className="station-checkbox-item" data-testid={`station-check-${s.id}`}>
                      <input type="checkbox"
                        checked={form.station_ids.includes(s.id)}
                        onChange={() => toggleStation(s.id)}
                      />
                      <span>{s.name}{s.station_type ? ` (${s.station_type})` : ''}</span>
                    </label>
                  ))}
                  {stations.length === 0 && <p className="text-muted small mb-0">No stations available.</p>}
                </div>
              </div>

              {/* Check availability feedback */}
              {checkResult === 'available' && (
                <div className="availability-notice available mb-3" data-testid="slots-available">
                  <i className="fa-solid fa-circle-check mr-2" />Station(s) available for the selected date and time.
                </div>
              )}
              {checkResult === 'conflict' && (
                <div className="availability-notice conflict mb-3" data-testid="slots-conflict">
                  <i className="fa-solid fa-triangle-exclamation mr-2" />Conflict detected — another run is booked at the same station and time.
                </div>
              )}

              {/* Buttons */}
              <div className="d-flex" style={{ gap: 8 }}>
                <button type="button" className="btn btn-outline-secondary flex-fill"
                  onClick={checkSlots} disabled={checking || form.station_ids.length === 0}
                  data-testid="btn-check-slots"
                >
                  <i className="fa-solid fa-magnifying-glass mr-1" />
                  {checking ? 'Checking...' : 'Check Slots'}
                </button>
                <button type="button" className="btn btn-teal flex-fill"
                  onClick={saveSchedule} disabled={scheduling}
                  data-testid="btn-save-schedule"
                >
                  <i className="fa-solid fa-save mr-1" />
                  {scheduling ? 'Saving...' : 'Save Schedule'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── RIGHT: Calendar + Active Batches ── */}
        <div className="col-xl-8 col-lg-7 mb-4">
          {/* Weekly Grid */}
          <WeeklyGrid
            runs={runs} stations={stations} brands={brands}
            weekOffset={weekOffset} setWeekOffset={setWeekOffset}
            onCardClick={setDetailRun}
          />

          {/* Active Batches */}
          <div className="card shadow-sm" data-testid="active-batches-card">
            <div className="card-header bg-white py-3 border-bottom">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="card-title mb-0 font-weight-bold text-dark">
                  <i className="fa-solid fa-gears mr-2 text-teal" />Active Batches &amp; Execution Control Room
                </h5>
                <span className="badge badge-light border px-2 py-1 text-muted small">
                  {filteredRuns.length} / {runs.length} runs
                </span>
              </div>
              {/* Filter bar */}
              <div className="d-flex flex-wrap align-items-center" style={{ gap: 6 }}>
                <select className="form-control form-control-sm custom-select" style={{ maxWidth: 140 }}
                  value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                  data-testid="filter-run-status"
                >
                  {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <select className="form-control form-control-sm custom-select" style={{ maxWidth: 150 }}
                  value={filterBrand} onChange={(e) => setFilterBrand(e.target.value)}
                  data-testid="filter-run-brand"
                >
                  <option value="">All Brands</option>
                  {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
                <input type="date" className="form-control form-control-sm" style={{ maxWidth: 140 }}
                  value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)}
                  title="Production date from" data-testid="filter-date-from"
                />
                <span className="text-muted small">→</span>
                <input type="date" className="form-control form-control-sm" style={{ maxWidth: 140 }}
                  value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)}
                  title="Production date to" data-testid="filter-date-to"
                />
                {(filterStatus !== 'ALL' || filterBrand || filterDateFrom || filterDateTo) && (
                  <button className="btn btn-sm btn-outline-secondary"
                    onClick={() => { setFilterStatus('ALL'); setFilterBrand(''); setFilterDateFrom(''); setFilterDateTo(''); }}
                    data-testid="btn-clear-filters"
                  >
                    <i className="fa-solid fa-xmark mr-1" />Clear
                  </button>
                )}
              </div>
            </div>
            <div className="card-body p-0">
              <div className="batches-scroll">
                <table className="table table-hover mb-0" data-testid="runs-table">
                  <thead className="thead-light">
                    <tr>
                      <th style={{ width: 110 }}>DATE &amp; TIME</th>
                      <th>BRAND &amp; SKU NAME</th>
                      <th className="text-center">TARGET QTY</th>
                      <th>ASSIGNED STATION(S)</th>
                      <th className="text-center">STATUS</th>
                      <th className="text-center">ACTIONS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRuns.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center text-muted py-4" data-testid="runs-empty">No production runs found.</td>
                      </tr>
                    )}
                    {filteredRuns.map((r) => {
                      const color = brandColor(r.brand_id, brands);
                      const sNames = r.station_names || [r.station_name].filter(Boolean);
                      return (
                        <tr key={r.id} data-testid={`run-row-${r.id}`}>
                          <td>
                            <div className="font-weight-bold" data-testid={`run-date-${r.id}`}>
                              {String(r.production_date).slice(0, 10)}
                            </div>
                            <small className="text-muted">{r.start_time}–{r.end_time}</small>
                          </td>
                          <td>
                            <div className="font-weight-bold" style={{ color }} data-testid={`run-brand-${r.id}`}>
                              {r.brand_name}
                            </div>
                            <small className="text-muted" data-testid={`run-item-${r.id}`}>
                              {r.item_code} — {r.item_name}
                            </small>
                          </td>
                          <td className="text-center" data-testid={`run-planned-${r.id}`}>
                            {Number(r.planned_yield).toFixed(0)}
                          </td>
                          <td data-testid={`run-stations-${r.id}`}>
                            {sNames.map((n) => (
                              <span key={n} className="badge badge-station mr-1 mb-1">{n}</span>
                            ))}
                          </td>
                          <td className="text-center">
                            <span className={`badge badge-status ${statusBadgeClass(r.status)}`} data-testid={`run-status-${r.id}`}>
                              {r.status.toUpperCase()}
                            </span>
                          </td>
                          <td className="text-center text-nowrap">
                            {r.status === 'Scheduled' && (
                              <>
                                <button className="btn btn-sm btn-warning mr-1" onClick={() => startRun(r.id)} data-testid={`btn-start-${r.id}`}>
                                  <i className="fa-solid fa-play mr-1" />Start
                                </button>
                                <button className="btn btn-sm btn-teal" onClick={() => openCompletion(r)} data-testid={`btn-log-${r.id}`}>
                                  <i className="fa-solid fa-check mr-1" />Log
                                </button>
                              </>
                            )}
                            {r.status === 'In Production' && (
                              <button className="btn btn-sm btn-teal" onClick={() => openCompletion(r)} data-testid={`btn-log-${r.id}`}>
                                <i className="fa-solid fa-check mr-1" />Log
                              </button>
                            )}
                            {r.status === 'Completed' && (
                              <span className="text-success small" data-testid={`run-done-${r.id}`}>
                                ✓ {Number(r.actual_yield).toFixed(0)} units
                              </span>
                            )}
                            {['Scheduled', 'In Production'].includes(r.status) && (
                              <button className="btn btn-sm btn-outline-danger ml-1" onClick={() => cancelRun(r.id)} data-testid={`btn-cancel-run-${r.id}`}>
                                Cancel
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Run Detail Modal ── */}
      <RunDetailModal
        run={detailRun}
        brands={brands}
        onClose={() => setDetailRun(null)}
        onStart={startRun}
        onComplete={(run) => { openCompletion(run); }}
        onCancel={cancelRun}
      />

      {/* ── Completion Modal ── */}
      {completionRun && (
        <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.5)' }} data-testid="completion-modal">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg border-0" style={{ borderRadius: 14 }}>
              <div className="modal-header modal-header-teal">
                <h5 className="modal-title font-weight-bold text-white">
                  <i className="fa-solid fa-circle-check mr-2" />Log Batch Completion &amp; Yields
                </h5>
                <button type="button" className="close text-white" onClick={() => setCompletionRun(null)} data-testid="completion-modal-close">
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body p-4">
                {/* Batch info */}
                <div className="bg-light rounded border p-3 mb-4">
                  <h6 className="font-weight-bold text-teal mb-2">Batch Target Blueprint</h6>
                  <div className="row" style={{ fontSize: '0.875rem' }}>
                    <div className="col-6 mb-1"><span className="text-muted">Brand:</span> <strong data-testid="comp-modal-brand">{completionRun.brand_name}</strong></div>
                    <div className="col-6 mb-1">
                      <span className="text-muted">SKU:</span> <strong data-testid="comp-modal-item">{completionRun.item_code} — {completionRun.item_name}</strong>
                    </div>
                    <div className="col-6"><span className="text-muted">Target Qty:</span> <strong className="text-teal" data-testid="comp-modal-planned">{Number(completionRun.planned_yield).toFixed(0)}</strong></div>
                    <div className="col-6">
                      <span className="text-muted">Station(s):</span>{' '}
                      {(completionRun.station_names || [completionRun.station_name].filter(Boolean)).map((n) => (
                        <span key={n} className="badge badge-station mr-1" data-testid="comp-modal-station">{n}</span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Negative stock warning */}
                {negativeWarning && (
                  <div className="alert alert-danger" data-testid="negative-stock-warning">
                    <strong>Warning:</strong> Ingredients will go negative after this deduction:
                    <ul className="mb-2 mt-2">
                      {negativeWarning.map((n) => (
                        <li key={n.item_id}>Available: {Number(n.available).toFixed(2)}, Needed: {Number(n.needed).toFixed(2)}</li>
                      ))}
                    </ul>
                    <button className="btn btn-danger btn-sm btn-block" onClick={() => submitCompletion(true)} disabled={completing} data-testid="btn-confirm-negative">
                      Confirm &amp; Complete Anyway
                    </button>
                  </div>
                )}

                {completionError && (
                  <div className="alert alert-danger py-2" data-testid="completion-error">{completionError}</div>
                )}

                {!negativeWarning && (
                  <>
                    {/* Actual yield */}
                    <div className="form-group">
                      <label className="font-weight-600">Actual Batch Yield Quantity <span className="text-danger">*</span></label>
                      <input type="number" className="form-control" min="1"
                        placeholder="Enter actual finished batch quantity"
                        value={actualYield} onChange={(e) => setActualYield(e.target.value)}
                        data-testid="actual-yield-qty"
                      />
                      {variancePct !== null && (
                        <small
                          className={`d-block mt-1 ${Math.abs(variancePct) > 10 ? 'text-danger font-weight-bold' : 'text-muted'}`}
                          data-testid="variance-hint"
                        >
                          Variance: {variancePct > 0 ? '+' : ''}{variancePct.toFixed(1)}%
                          {variancePct > 0 ? ' (Surplus)' : ' (Deficit)'}
                          {Math.abs(variancePct) > 10 ? ' — Justification required' : ''}
                        </small>
                      )}
                    </div>

                    {/* Quality Evaluation */}
                    <div className="form-group">
                      <label className="font-weight-600">Quality Evaluation Log</label>
                      <select className="form-control custom-select" value={qualityEval}
                        onChange={(e) => setQualityEval(e.target.value)}
                        data-testid="quality-eval"
                      >
                        <option value="">Select evaluation...</option>
                        {QUALITY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>

                    {/* Variance note */}
                    {needsVarianceNote && (
                      <div className="form-group" data-testid="discrepancy-note-group">
                        <label className="font-weight-600 text-danger">
                          <i className="fa-solid fa-triangle-exclamation mr-1" />
                          Yield Variance &gt;10% — Explanatory Note <span className="text-danger">*</span>
                        </label>
                        <textarea className="form-control border-danger" rows={2}
                          placeholder="Why is there >10% variance from planned production? (Required)"
                          value={varianceNote} onChange={(e) => setVarianceNote(e.target.value)}
                          data-testid="discrepancy-note"
                        />
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary" onClick={() => setCompletionRun(null)} data-testid="completion-modal-cancel">Cancel</button>
                {!negativeWarning && (
                  <button type="button" className="btn btn-teal px-4" onClick={() => submitCompletion()} disabled={completing} data-testid="btn-submit-completion">
                    <i className="fa-solid fa-check mr-1" />{completing ? 'Submitting...' : 'Submit Completion'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
